import { useState } from 'react';

import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
    type NotesSettingsPatch,
    type NotesSettingsValue,
    notesSettingsOptions,
    useUpdateNotesSettingsOptions,
} from '@/entities/practice-member-settings';

import { DEFAULT_ERROR_MESSAGE } from '../config/notes-settings-options';

export function useNotesSetting<F extends keyof NotesSettingsValue>(
    field: F,
    errorMessage: string = DEFAULT_ERROR_MESSAGE,
) {
    const { data: settings } = useSuspenseQuery(notesSettingsOptions());
    const mutation = useMutation(useUpdateNotesSettingsOptions());

    // Wins over the query once set, so the mutation's own cache write can't revert an edit that
    // is still in flight. Cleared on error only — clearing on success could let an earlier success
    // briefly show over a later write still in flight.
    const [pending, setPending] = useState<NotesSettingsValue[F]>();
    const value = pending ?? settings.value[field];

    const save = (next: NotesSettingsValue[F]) => {
        setPending(next);
        mutation.mutate({ [field]: next } as NotesSettingsPatch, {
            onError: () => {
                toast.error(errorMessage);
                setPending(undefined);
            },
        });
    };

    return { value, save };
}
